import { createRouter, createWebHistory } from 'vue-router';
import isEmpty from 'lodash/isEmpty';
import NProgress from 'nprogress';
import { store } from '@/state/store.js';
import { routes } from '@/routes/routes.js';
import { handleErrors } from '@/utils/errors.js';

const router = createRouter({
  routes,
  history: createWebHistory('/'),
  linkActiveClass: 'active',

  // Simulate native-like scroll behavior when navigating to a new
  // route and using back/forward buttons.
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      // savedPosition is only available for popstate navigations.
      return savedPosition;
    } else {
      let position = {
        behavior: 'smooth',
      };

      // scroll to anchor by returning the selector
      if (to.hash) {
        position.el = to.hash;

        // check for element matching the hash
        try {
          if (document.querySelector(to.hash)) {
            return position;
          }
        } catch (e) {
          return false;
        }

        // if the returned position is falsy or an empty object,
        // will retain current scroll position.
        return false;
      }

      // coords will be used if no selector is provided
      position.left = 0;
      position.top = 0;

      return position;
    }
  },
});

router.beforeEach((to, from, next) => {
  NProgress.start();

  // Check this route permission and all parent route permissions against the auth user permission
  const canAccess = to.matched.every((route) => {
    if (!route.meta.permission) {
      return true;
    }

    return store.getters['auth/can'](route.meta.permission);
  });

  // if no access throw an error
  if (!canAccess) {
    let err = new Error('Forbidden');
    err.response = { status: 403 };
    return next(err);
  }

  // if there isn't a query string, and we have a querystring saved
  // then load it into the route and go there unless we're coming
  // from and to the same route then just load as normal and
  // ignore the stored filter because we're assuming this
  // is because the user explicitly changed something.
  const storedFilter = store.state.filterBuilder.queries[to.name];
  const hasStoredFilter = storedFilter && !isEmpty(storedFilter);
  const isSameRoute = from.name === to.name;

  if (!isSameRoute && isEmpty(to.query) && hasStoredFilter) {
    for (let prop in storedFilter) {
      if (Object.prototype.hasOwnProperty.call(storedFilter, prop)) {
        //update the 'to' route to include the stored querystring
        to.query[prop] = storedFilter[prop];
      }
    }

    // change the actual next route to this updated 'to' route
    return next(to);
  }

  // If we've marked a particular route to save the query string in the session
  if (to.meta.storeQuery) {
    store.dispatch('filterBuilder/storeQuery', {
      name: to.name,
      query: to.query,
    });
  }

  next();
});

/**
 * Create a `beforeResolve` hook, which fires whenever `beforeRouteEnter` and `beforeRouteUpdate`
 * would. This allows us to ensure data is fetched even when params change, but the resolved
 * route does not. We put it in `meta` to indicate that it's a hook we created, rather than
 * part of Vue Router (yet?).
 * @param {Object} to - vue-router: the target Route Object being navigated to.
 * @param {Object} from - vue-router: the current route being navigated away from.
 * @param {Function} next - vue-router: this function must be called to resolve the hook.
 */
router.beforeResolve(async (to, from, next) => {
  try {
    // For each matched route...
    for (const route of to.matched) {
      await new Promise((resolve, reject) => {
        // If a `beforeResolve` hook is defined, call it with
        // the same arguments as the `beforeEnter` hook.
        if (route.meta && route.meta.beforeResolve) {
          route.meta.beforeResolve(to, from, (...args) => {
            // If the user chose to redirect...
            if (args.length) {
              // If redirecting to the same route we're coming from...
              if (from.name === args[0].name) {
                // Complete the animation of the route progress bar.
                NProgress.done();
              }
              // Complete the redirect.
              next(...args);
              reject(new Error('Redirected'));
            } else {
              resolve();
            }
          });
        } else {
          // Otherwise, continue resolving the route.
          resolve();
        }
      });
    }

    // If a `beforeResolve` hook chose to redirect, just return.
  } catch (error) {
    return;
  }

  // If we reach this point, continue resolving the route.
  next();
});

// When each route is finished evaluating...
router.afterEach(() => {
  return NProgress.done();
});

router.onError((error) => {
  NProgress.done();
  return handleErrors(error);
});

export { router };
